import React, { useState, useEffect, useRef } from 'react';
import { Button } from './ui/button';
import { Card, CardContent } from './ui/card';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { X, Camera, CaretDown, CaretUp, Plus, Trash, Check } from '@phosphor-icons/react';
import axios from 'axios';
import { toast } from 'sonner';
import BirthdayDecoratorSelector from './BirthdayDecoratorSelector';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * Package-per-event decorator quick add.
 *
 * Vendor builds packages, each scoped to ONE event:
 *  - Marriage / Engagement / Reception: must tick ≥1 wedding tradition
 *    (Hindu / Muslim / Christian / Brahmin), tick 1-2 venue types, optional
 *    "fresh flowers included" flag, 2-5 photos, price.
 *  - Birthday: routes to the existing BirthdayDecoratorSelector
 *    component (themes + addons).
 *  - Every other event (Baby Shower, House Warming, Corporate, Anniversary,
 *    Puberty Ceremony, Retirement, Religious/Pooja, Other): 1-3 photos + price.
 *
 * The customer-side filter matches on (event_type, traditions, venue_type)
 * so a Hindu Mandapam wedding only sees vendors whose packages tick both.
 */

const EVENT_OPTIONS = [
  { key: 'marriage', label: 'Marriage' },
  { key: 'engagement', label: 'Engagement / Reception' },
  { key: 'birthday', label: 'Birthday' },
  { key: 'baby_shower', label: 'Baby Shower / Seemantham' },
  { key: 'house_warming', label: 'House Warming' },
  { key: 'corporate', label: 'Corporate Event' },
  { key: 'anniversary', label: 'Anniversary' },
  { key: 'puberty_ceremony', label: 'Puberty Ceremony' },
  { key: 'retirement', label: 'Retirement Party' },
  { key: 'religious', label: 'Religious / Pooja' },
  { key: 'other', label: 'Other Events' },
];

const WEDDING_EVENTS = new Set(['marriage', 'engagement']);

const WEDDING_TRADITIONS = [
  { value: 'hindu', label: 'Hindu Marriage' },
  { value: 'muslim', label: 'Muslim Wedding' },
  { value: 'christian', label: 'Christian Wedding' },
  { value: 'brahmin', label: 'Brahmin Wedding' },
];

const DecoratorSelector = ({ onSave, initialPackages = [] }) => {
  const [venueTypes, setVenueTypes] = useState([]);
  const [packages, setPackages] = useState(initialPackages.length ? initialPackages : []);
  const fileInputRef = useRef(null);
  const [uploadTarget, setUploadTarget] = useState(null);
  const [uploading, setUploading] = useState(false);

  useEffect(() => {
    const fetchVenueTypes = async () => {
      try {
        const { data } = await axios.get(`${BACKEND_URL}/api/quick-add-items?vendor_category=venue`);
        setVenueTypes((data || []).filter(i => i.item_type === 'venue_type').map(v => v.name));
      } catch {
        // Sensible default if API not reachable
        setVenueTypes(['Mandapam', 'Convention Center', 'Marriage Hall', 'Convention Hall', 'Garden / Hotel', 'Villa', 'Banquet / Auditorium', 'Rooftop / Studio']);
      }
    };
    fetchVenueTypes();
  }, []);

  const addPackage = () => {
    setPackages([...packages, {
      name: '',
      event_type: '',
      expanded: true,
      traditions: [],
      venue_types: [],
      fresh_flowers: false,
      photos: [],
      price: '',
    }]);
  };

  const removePackage = (idx) => {
    if (window.confirm('Remove this package?')) {
      setPackages(packages.filter((_, i) => i !== idx));
    }
  };

  const updateField = (idx, field, value) => {
    const u = [...packages];
    u[idx][field] = value;
    setPackages(u);
  };

  const toggleListItem = (idx, field, value, max) => {
    const u = [...packages];
    const cur = u[idx][field] || [];
    if (cur.includes(value)) {
      u[idx][field] = cur.filter(v => v !== value);
    } else {
      if (max && cur.length >= max) {
        toast.error(`You can pick max ${max}`);
        return;
      }
      u[idx][field] = [...cur, value];
    }
    setPackages(u);
  };

  const onPickPhoto = async (file) => {
    if (!file || !uploadTarget) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const { data } = await axios.post(`${BACKEND_URL}/api/upload`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      const url = data?.url || data?.file_url;
      if (!url) { toast.error('Upload failed'); setUploading(false); return; }
      const { pkgIdx } = uploadTarget;
      const u = [...packages];
      const isWedding = WEDDING_EVENTS.has(u[pkgIdx].event_type);
      const max = isWedding ? 5 : 3;
      if ((u[pkgIdx].photos || []).length >= max) {
        toast.error(`Max ${max} photos for this event`);
        setUploading(false);
        return;
      }
      u[pkgIdx].photos = [...(u[pkgIdx].photos || []), url];
      setPackages(u);
      toast.success('Photo uploaded');
    } catch (e) {
      toast.error('Upload failed');
    } finally {
      setUploading(false);
      setUploadTarget(null);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const removePhoto = (pkgIdx, photoIdx) => {
    const u = [...packages];
    u[pkgIdx].photos = u[pkgIdx].photos.filter((_, i) => i !== photoIdx);
    setPackages(u);
  };

  // Validation gates the Save button
  const validatePackage = (p) => {
    if (!p.event_type) return 'Pick an event';
    if (p.event_type === 'birthday') return null; // birthday handled separately
    const isWedding = WEDDING_EVENTS.has(p.event_type);
    const minPhotos = isWedding ? 2 : 1;
    const maxPhotos = isWedding ? 5 : 3;
    if (isWedding && (p.traditions || []).length === 0) return 'Tick at least one wedding tradition';
    if (isWedding && (p.venue_types || []).length === 0) return 'Tick at least one venue type';
    if (isWedding && (p.venue_types || []).length > 2) return 'Maximum 2 venue types per package';
    if ((p.photos || []).length < minPhotos) return `Upload at least ${minPhotos} photo${minPhotos === 1 ? '' : 's'}`;
    if ((p.photos || []).length > maxPhotos) return `Maximum ${maxPhotos} photos`;
    if (!Number(p.price) || Number(p.price) <= 0) return 'Enter a valid price';
    return null;
  };

  const onSaveAll = () => {
    if (packages.length === 0) {
      toast.error('Add at least one package');
      return;
    }
    for (let i = 0; i < packages.length; i++) {
      const p = packages[i];
      if (p.event_type === 'birthday') continue;
      const err = validatePackage(p);
      if (err) {
        toast.error(`Package ${i + 1}: ${err}`);
        return;
      }
    }
    // Auto-name packages if blank
    const final = packages.map((p, i) => ({
      ...p,
      name: p.name || `${EVENT_OPTIONS.find(e => e.key === p.event_type)?.label || 'Package'} ${i + 1}`,
    }));
    onSave({ packages: final });
  };

  // For Birthday event packages, embed the existing BirthdayDecoratorSelector inline
  const renderBirthdayPanel = (idx, p) => (
    <div className="mt-3 p-3 border border-[#E8E1D5] rounded bg-sand-50">
      <p className="text-xs text-text-secondary mb-2">
        Birthday packages use the dedicated theme + add-on builder below.
        Save once the theme is configured — it will be merged with your other packages on the next "Save All" click.
      </p>
      <BirthdayDecoratorSelector
        onSave={(birthdayData) => {
          const u = [...packages];
          u[idx].birthday_data = birthdayData;
          setPackages(u);
          toast.success('Birthday theme saved into this package');
        }}
      />
    </div>
  );

  return (
    <div className="space-y-4">
      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        className="hidden"
        onChange={(e) => onPickPhoto(e.target.files?.[0])}
        data-testid="decor-photo-input"
      />

      <div className="flex items-center justify-between border-b border-[#E8E1D5] pb-2">
        <p className="text-sm text-text-secondary">
          Build a package for each event you cater. Customers will only see your package for the events / traditions / venue types you tick.
        </p>
        <Button type="button" onClick={addPackage} className="bg-brand-primary hover:bg-[#A84A38] text-sand-50 rounded-none" data-testid="add-package-btn">
          <Plus size={14} className="mr-1" /> Add Package
        </Button>
      </div>

      {packages.length === 0 && (
        <div className="text-center py-10 text-text-secondary border border-dashed border-[#E8E1D5] rounded">
          <p className="text-sm mb-3">No packages yet</p>
          <Button type="button" onClick={addPackage} className="bg-brand-primary hover:bg-[#A84A38] text-sand-50 rounded-none">
            <Plus size={14} className="mr-1" /> Create your first package
          </Button>
        </div>
      )}

      {packages.map((p, idx) => {
        const isWedding = WEDDING_EVENTS.has(p.event_type);
        const isBirthday = p.event_type === 'birthday';
        const minPhotos = isWedding ? 2 : 1;
        const maxPhotos = isWedding ? 5 : 3;
        return (
          <Card key={idx} className="border-[#E8E1D5]" data-testid={`decor-package-${idx}`}>
            <CardContent className="p-4 space-y-3">
              <div className="flex items-center justify-between">
                <button type="button" onClick={() => updateField(idx, 'expanded', !p.expanded)} className="flex items-center gap-2 text-left">
                  {p.expanded ? <CaretUp size={16} /> : <CaretDown size={16} />}
                  <span className="font-heading text-lg">
                    Package {idx + 1}
                    {p.event_type && <span className="text-sm text-text-secondary font-normal ml-2">— {EVENT_OPTIONS.find(e => e.key === p.event_type)?.label}</span>}
                  </span>
                </button>
                <Button type="button" variant="ghost" size="sm" onClick={() => removePackage(idx)} className="text-red-500" data-testid={`remove-package-${idx}`}>
                  <Trash size={14} />
                </Button>
              </div>

              {p.expanded && (
                <div className="space-y-3">
                  {/* Package name */}
                  <div>
                    <Label className="text-xs tracking-[0.2em] uppercase font-bold text-text-secondary">Package name (optional)</Label>
                    <Input type="text" value={p.name} onChange={(e) => updateField(idx, 'name', e.target.value)} placeholder="e.g. Premium Hindu Mandapam Wedding" className="rounded-none border-[#E8E1D5]" data-testid={`pkg-name-${idx}`} />
                  </div>

                  {/* Event picker */}
                  <div>
                    <Label className="text-xs tracking-[0.2em] uppercase font-bold text-text-secondary mb-1 block">Event type *</Label>
                    <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-2">
                      {EVENT_OPTIONS.map(ev => {
                        const active = p.event_type === ev.key;
                        return (
                          <button type="button" key={ev.key} onClick={() => updateField(idx, 'event_type', ev.key)} className={`text-left p-2 border-2 rounded text-sm transition-all ${active ? 'border-brand-primary bg-brand-accent/10 ring-1 ring-brand-primary' : 'border-[#E8E1D5] hover:border-brand-primary/40'}`} data-testid={`pkg-event-${idx}-${ev.key}`}>
                            {ev.label}
                          </button>
                        );
                      })}
                    </div>
                  </div>

                  {/* Birthday branch */}
                  {isBirthday && renderBirthdayPanel(idx, p)}

                  {/* Common branch — wedding + non-birthday */}
                  {p.event_type && !isBirthday && (
                    <>
                      {/* Wedding traditions */}
                      {isWedding && (
                        <div className="border border-brand-primary/30 rounded p-2 bg-brand-accent/5">
                          <Label className="text-xs tracking-[0.2em] uppercase font-bold text-brand-primary mb-1 block">Wedding Tradition * (tick all that apply)</Label>
                          <div className="grid grid-cols-2 gap-2">
                            {WEDDING_TRADITIONS.map(t => {
                              const checked = (p.traditions || []).includes(t.value);
                              return (
                                <label key={t.value} className={`flex items-center gap-2 p-2 border-2 rounded cursor-pointer text-sm transition-all ${checked ? 'border-brand-primary bg-white' : 'border-[#E8E1D5] hover:border-brand-primary/40'}`} data-testid={`pkg-tradition-${idx}-${t.value}`}>
                                  <input type="checkbox" checked={checked} onChange={() => toggleListItem(idx, 'traditions', t.value)} className="h-4 w-4 accent-brand-primary" />
                                  <span>{t.label}</span>
                                </label>
                              );
                            })}
                          </div>
                        </div>
                      )}

                      {/* Venue types (wedding only) */}
                      {isWedding && (
                        <div>
                          <Label className="text-xs tracking-[0.2em] uppercase font-bold text-text-secondary mb-1 block">Venue Type * (tick 1–2)</Label>
                          <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                            {venueTypes.map(vt => {
                              const checked = (p.venue_types || []).includes(vt);
                              return (
                                <label key={vt} className={`flex items-center gap-2 p-2 border-2 rounded cursor-pointer text-sm transition-all ${checked ? 'border-brand-primary bg-brand-accent/5' : 'border-[#E8E1D5] hover:border-brand-primary/40'}`} data-testid={`pkg-venue-${idx}-${vt.replace(/[^a-z0-9]+/gi,'-').toLowerCase()}`}>
                                  <input type="checkbox" checked={checked} onChange={() => toggleListItem(idx, 'venue_types', vt, 2)} className="h-4 w-4 accent-brand-primary" />
                                  <span>{vt}</span>
                                </label>
                              );
                            })}
                          </div>
                          <p className="text-[10px] text-text-secondary mt-1">{(p.venue_types || []).length}/2 selected</p>
                        </div>
                      )}

                      {/* Fresh flowers (wedding only) */}
                      {isWedding && (
                        <label className="flex items-center gap-2 p-2 border-2 border-[#E8E1D5] rounded cursor-pointer text-sm" data-testid={`pkg-fresh-flowers-${idx}`}>
                          <input type="checkbox" checked={p.fresh_flowers} onChange={(e) => updateField(idx, 'fresh_flowers', e.target.checked)} className="h-4 w-4 accent-brand-primary" />
                          <span>Fresh flower decoration is included in this package</span>
                        </label>
                      )}

                      {/* Photos */}
                      <div>
                        <Label className="text-xs tracking-[0.2em] uppercase font-bold text-text-secondary mb-1 block">
                          Photos * ({minPhotos}–{maxPhotos})
                        </Label>
                        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-2">
                          {(p.photos || []).map((ph, i) => (
                            <div key={i} className="relative group">
                              <img src={ph.startsWith('http') ? ph : `${BACKEND_URL}${ph}`} alt="" className="w-full h-24 object-cover border border-[#E8E1D5]" />
                              <button type="button" onClick={() => removePhoto(idx, i)} className="absolute -top-1 -right-1 bg-red-500 text-white rounded-full w-5 h-5 flex items-center justify-center opacity-0 group-hover:opacity-100" data-testid={`pkg-photo-remove-${idx}-${i}`}>
                                <X size={10} />
                              </button>
                            </div>
                          ))}
                          {(p.photos || []).length < maxPhotos && (
                            <button type="button" onClick={() => { setUploadTarget({ pkgIdx: idx }); fileInputRef.current?.click(); }} disabled={uploading} className="border-2 border-dashed border-[#E8E1D5] hover:border-brand-primary rounded h-24 flex flex-col items-center justify-center text-xs text-text-secondary gap-1" data-testid={`pkg-photo-add-${idx}`}>
                              <Camera size={20} />
                              <span>{uploading ? 'Uploading...' : 'Add photo'}</span>
                            </button>
                          )}
                        </div>
                      </div>

                      {/* Price */}
                      <div>
                        <Label className="text-xs tracking-[0.2em] uppercase font-bold text-text-secondary">Price * (₹)</Label>
                        <Input type="number" value={p.price} onChange={(e) => updateField(idx, 'price', e.target.value)} placeholder="e.g. 150000" className="rounded-none border-[#E8E1D5]" data-testid={`pkg-price-${idx}`} />
                      </div>
                    </>
                  )}
                </div>
              )}
            </CardContent>
          </Card>
        );
      })}

      {packages.length > 0 && (
        <div className="flex justify-end pt-2 border-t border-[#E8E1D5]">
          <Button type="button" onClick={onSaveAll} className="bg-brand-primary hover:bg-[#A84A38] text-sand-50 rounded-none" data-testid="save-decor-packages-btn">
            <Check size={14} className="mr-1" /> Save All Packages ({packages.length})
          </Button>
        </div>
      )}
    </div>
  );
};

export default DecoratorSelector;
